import { useCallback, useEffect, useState } from "react";
import useSWR from "swr";
import { fetchDataGroupBoards } from "../../services/monday-service";
import logger from "../../lib/logger";
import type { GroupType } from "../../components/addItem/GroupsList";

const SWR_KEY_PREFIX = "board-groups-";

export type BoardGroupsMap = Record<string, GroupType[]>;

async function fetcher(boardId: string): Promise<GroupType[]> {
  const result = await fetchDataGroupBoards(boardId);
  return Array.isArray(result) ? result : [];
}

export function useBoardGroups() {
  const [boardGroups, setBoardGroups] = useState<BoardGroupsMap>({});
  const [requestedBoardId, setRequestedBoardId] = useState<string | null>(null);

  const swrKey = requestedBoardId ? `${SWR_KEY_PREFIX}${requestedBoardId}` : null;
  const { data, isLoading, error, mutate } = useSWR<GroupType[]>(
    swrKey,
    () => fetcher(requestedBoardId!),
    { revalidateOnFocus: false },
  );

  useEffect(() => {
    if (data && requestedBoardId) {
      setBoardGroups((prev) => ({ ...prev, [requestedBoardId]: data }));
    }
  }, [data, requestedBoardId]);

  useEffect(() => {
    if (error) {
      logger.error("Error fetching board groups", { error, boardId: requestedBoardId });
    }
  }, [error, requestedBoardId]);

  const fetchGroupsForBoard = useCallback((boardId: string) => {
    setRequestedBoardId(boardId);
  }, []);

  const loadingBoardId = isLoading && requestedBoardId ? requestedBoardId : null;
  const errorBoardId = error && requestedBoardId ? requestedBoardId : null;

  return {
    boardGroups,
    fetchGroupsForBoard,
    loadingBoardId,
    errorBoardId,
    isLoadingGroups: isLoading,
    error,
    mutateBoardGroups: mutate,
  };
}
